/* eslint-disable react-hooks/exhaustive-deps */
// AuthGuard.tsx
import { Suspense, useEffect, useMemo, useState, type ReactNode } from "react";
// Fires onReady once after children mount (useful to detect lazy import readiness)
type OnceMountedProps = { onReady?: () => void; children: ReactNode };
function OnceMounted({ onReady, children }: OnceMountedProps) {
  useEffect(() => { onReady?.(); }, []);
  return <>{children}</>;
}
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Pages } from "@/utils/routes.utils";
import { AuthStore } from "@/store/auth/auth.store";
import { decrypt } from "@/utils/encryption.utils";
import { useSignInWithHash } from "@/store/auth/auth.service";
import { useBrandConfig } from "@/utils/useBrandConfig";
import { getCustomComponent } from "@/utils/custom-components.utils";
import { useResponsive } from "@/utils/useResponsive";
import { useGetBrandRelations } from "@/store/brand-relations/brand-relations.service";
import { WalletStore } from "@/store/wallet/wallet.store";

/**
 * Extracts email, id, iv, and redirectUrl parameters from the URL
 * @returns Object containing email, id, iv, and redirectUrl (or null if not present)
 */
const getUrlParams = (): {
  email: string | undefined;
  id: string | undefined;
  iv: string | undefined;
  redirectUrl: string | undefined;
} => {
  // Use the raw query string to preserve + characters
  const searchParams = window.location.search.substring(1);
  const params: Record<string, string> = {};

  if (searchParams) {
    searchParams.split('&').forEach(param => {
      const [key, value] = param.split('=');
      if (key && value) {
        // Decode only URL encoding, but preserve + as +, not space
        params[decodeURIComponent(key)] = decodeURIComponent(value);
      }
    });
  }

  return {
    email: params['email'] || undefined,
    id: params['id'] || undefined,
    iv: params['iv'] || undefined,
    redirectUrl: params['redirectUrl'] || undefined,
  };
};

function useQuery() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}

export const AuthGuard = () => {
  const { getSession, signOut } = AuthStore();

  // Check if we have email and iv in URL to initialize loading state
  const { email: urlEmail, iv: urlIv } = getUrlParams();
  const [isAuth, setIsAuth] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(!!urlEmail && !!urlIv);
  const { isMobile } = useResponsive();
  const [headerReady, setHeaderReady] = useState(false);
  const [footerReady, setFooterReady] = useState(false);

  const { pathname } = useLocation();
  const initialPage = pathname.substring(1).toUpperCase();

  const token = useQuery().get('token');

  const signInWithHash = useSignInWithHash({
    onSuccess: () => {
      setIsAuth(true);
      setIsLoading(false);
    },
    onError: () => {
      const redirectUrl = sessionStorage.getItem('_redirectUrl');
      if (redirectUrl) {
        sessionStorage.removeItem('_redirectUrl');
        window.location.href = redirectUrl;
        return;
      }
      setIsLoading(false);
      signOut();
      setIsAuth(false);
    }
  });

  useEffect(() => {
    (async () => {
      // Extract and save URL params to AuthStore
      const { email, id, iv, redirectUrl } = getUrlParams();
      if (email && iv) {
        // Save redirectUrl to sessionStorage if present
        if (redirectUrl) {
          sessionStorage.setItem('_redirectUrl', redirectUrl);
        }

        // Remove params from URL
        const url = new URL(window.location.href);
        url.searchParams.delete('email');
        url.searchParams.delete('id');
        url.searchParams.delete('iv');
        url.searchParams.delete('redirectUrl');
        window.history.replaceState({}, '', url.toString());

        // Sign in with hash
        signInWithHash.mutate({ email, iv, id });
        return;
      }

      if (token) {
        const payload = await decrypt(token);
        if (payload) {
          sessionStorage.setItem('_session', JSON.stringify(payload))
        }
      }

      // Only check session if not processing email/iv
      const session = await getSession();
      setIsAuth(!!session);
    })();
  }, [token]);

  const { name, multiBrandEnabled } = useBrandConfig();
  const setBrandRelations = WalletStore((state) => state.setBrandRelations);
  const { data: brandRelationsData } = useGetBrandRelations(!!multiBrandEnabled && !!isAuth);

  useEffect(() => {
    if (brandRelationsData) {
      setBrandRelations(brandRelationsData);
    }
  }, [brandRelationsData, setBrandRelations]);

  const Header = useMemo(() => getCustomComponent({
    brandName: name,
    folder: 'Header',
  }), [name]);

  const Footer = useMemo(() => getCustomComponent({
    brandName: name,
    folder: 'Footer',
  }), [name]);

  // Reserve scrollbar space from the start to prevent width change when content becomes scrollable.
  useEffect(() => {
    // Force scrollbar track so layout width stays constant (eliminates loader horizontal shift).
    const prevHtmlOverflowY = document.documentElement.style.overflowY;
    document.documentElement.style.overflowY = 'scroll';
    return () => { document.documentElement.style.overflowY = prevHtmlOverflowY; };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }
  if (isAuth === null) return null;
  return isAuth ?
    <div className="min-h-dvh w-full flex flex-col justify-between overflow-x-hidden" data-header-ready={headerReady} data-footer-ready={footerReady}>
      {/* Global loader overlay while custom Header/Footer are loading */}
      {!(headerReady && footerReady) && (
        <div
          className="fixed inset-0 z-[9999] grid place-items-center bg-white"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#0000C8] border-t-transparent will-change-transform" aria-label="Loading" />
        </div>
      )}
      {/* Wrapper that fires once when lazy component mounts (i.e., import finished) */}
      <Suspense
        fallback={
          isMobile ? <div className="h-[40px]" /> : <div className="h-[80px]" />
        }
      >
        {/* Detect when Header is ready */}
        <OnceMounted onReady={() => { setHeaderReady(true); }}>
          <Header
            initialPage={initialPage as "WALLET" | "REWARDS" || "SETTINGS"}
          />
        </OnceMounted>
      </Suspense>
      <Outlet />
      <Suspense>
        {/* Detect when Footer is ready */}
        <OnceMounted onReady={() => { setFooterReady(true); }}>
          <Footer />
        </OnceMounted>
      </Suspense>
    </div> : <Navigate to={Pages.SINGIN} />;
};

export const GuestGuard = () => {
  const { getSession, signOut } = AuthStore();

  // Check if we have email and iv in URL to initialize loading state
  const { email: urlEmail, iv: urlIv } = getUrlParams();
  const [isAuth, setIsAuth] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(!!urlEmail && !!urlIv);

  const token = useQuery().get('token');

  const signInWithHash = useSignInWithHash({
    onSuccess: () => {
      setIsAuth(true);
      setIsLoading(false);
    },
    onError: (error) => {
      console.error('Failed to sign in with hash:', error);
      const redirectUrl = sessionStorage.getItem('_redirectUrl');
      if (redirectUrl) {
        sessionStorage.removeItem('_redirectUrl');
        window.location.href = redirectUrl;
        return;
      } else {
        setIsLoading(false);
        signOut();
        setIsAuth(false);
      }
    }
  });

  useEffect(() => {
    (async () => {
      // Extract and save URL params to AuthStore
      const { email, id, iv, redirectUrl } = getUrlParams();
      if (email && iv) {
        // Save redirectUrl to sessionStorage if present
        if (redirectUrl) {
          sessionStorage.setItem('_redirectUrl', redirectUrl);
        }

        // Remove params from URL
        const url = new URL(window.location.href);
        url.searchParams.delete('email');
        url.searchParams.delete('id');
        url.searchParams.delete('iv');
        url.searchParams.delete('redirectUrl');
        window.history.replaceState({}, '', url.toString());

        // Sign in with hash
        signInWithHash.mutate({ email, iv, id });
        return;
      }

      if (token) {
        const payload = await decrypt(token);
        if (payload) {
          sessionStorage.setItem('_session', JSON.stringify(payload))
        }
      }

      // Only check session if not processing email/iv
      const session = await getSession();
      setIsAuth(!!session);
    })();
  }, [token]);

  if (isLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  } if (isAuth === null) return null;
  return isAuth ? <Navigate to={Pages.HOME} /> : <Outlet />;
};
